import { FaFileExport } from 'react-icons/fa';
import {
  MdCloudDownload,
  MdContentCopy,
  MdDelete,
  MdMoreVert,
} from 'react-icons/md';
import { Menu } from '@headlessui/react';
import {
  Button,
  DropdownDivider,
  DropdownItem,
  DropdownItems,
  Folder,
  Icon,
  usePopperDropdown,
} from '@nbirl/nbi-common-ui-libs';
import { Portal } from '@reach/portal';

import { useCurrentUser, useDownload } from '../../../../../shared/context';
import { Permission } from '../../../../../shared/enum';

type FolderDropdownProps = {
  canCopyFolder: boolean;
  selectedFolder: Folder;
  isSubFolder?: boolean;
  size?: 'md' | 'sm';
  variant?: React.ComponentProps<typeof Button>['variant'];
  onShowCopyModal: () => void;
  onShowMoveModal: () => void;
  onShowDeleteModal: () => void;
};

export const FolderDropdownMenu: React.FC<FolderDropdownProps> = ({
  canCopyFolder,
  selectedFolder,
  isSubFolder,
  size,
  variant,
  onShowCopyModal,
  onShowMoveModal,
  onShowDeleteModal,
}) => {
  const { permissions } = useCurrentUser();
  const { referenceElementProps, popperElementProps } = usePopperDropdown({
    placement: 'right-start',
  });
  const { isDownloading, download } = useDownload();

  const handleDownload = () => {
    download({ documentIds: [], folderIds: [selectedFolder.id] });
  };

  return (
    <Menu>
      <Menu.Button
        as={Button}
        variant={variant}
        aria-label={isSubFolder ? 'Folder menu' : 'Main folder menu'}
        size="sm"
        onlyIcon
        {...referenceElementProps}
      >
        <Icon size={size === 'sm' ? '18' : '20'} icon={<MdMoreVert />} />
      </Menu.Button>

      <Portal>
        <DropdownItems {...popperElementProps}>
          {canCopyFolder && (
            <DropdownItem
              icon={
                <Icon
                  icon={<MdContentCopy className="text-gray-300" />}
                  size="18"
                />
              }
              onClick={onShowCopyModal}
            >
              Copy folder
            </DropdownItem>
          )}

          {isSubFolder && permissions.includes(Permission.ManageFolders) && (
            <DropdownItem
              icon={
                <Icon
                  icon={<FaFileExport className="text-gray-300" />}
                  size="18"
                />
              }
              onClick={onShowMoveModal}
            >
              Move to
            </DropdownItem>
          )}

          <DropdownItem
            icon={
              <Icon
                icon={<MdCloudDownload className="text-gray-300" />}
                size="18"
              />
            }
            onClick={handleDownload}
            disabled={isDownloading}
          >
            Download
          </DropdownItem>

          {permissions.includes(Permission.DeleteFolder) && (
            <>
              <DropdownDivider />

              <DropdownItem
                icon={<Icon icon={<MdDelete />} size="18" />}
                variant="danger"
                onClick={onShowDeleteModal}
              >
                Delete folder
              </DropdownItem>
            </>
          )}
        </DropdownItems>
      </Portal>
    </Menu>
  );
};
